import { Box, Button, Typography, useTheme } from "@mui/material";
import { FC, useMemo, useState } from "react";
import {
  getDate,
  getDay,
  getEndOfWeek,
  getMonth,
  getStartOfWeek,
  getYear,
  isSameWeek,
} from "../../../../shared/utils/date";
import { BORDER_RADIUS } from "../constants";

const WEEKDAY_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

interface WeeksGridProps {
  selectedYear: number;
  selectedMonth: number;
  startDate: Date | null;
  endDate: Date | null;
  onDayClick: (date: Date) => void;
  earliestSelectableDate?: Date;
}

const toIsoDayOfWeek = (sundayBasedDay: number): number =>
  sundayBasedDay === 0 ? 6 : sundayBasedDay - 1;

const generateCalendarDays = (year: number, month: number): Date[] => {
  const firstDayOfMonth = new Date(Date.UTC(year, month, 1));
  const firstWeekdayISO = toIsoDayOfWeek(getDay(firstDayOfMonth));

  const calendarStart = new Date(firstDayOfMonth);
  calendarStart.setUTCDate(calendarStart.getUTCDate() - firstWeekdayISO);

  return Array.from({ length: 42 }, (_, i) => {
    const day = new Date(calendarStart);
    day.setUTCDate(day.getUTCDate() + i);

    return day;
  });
};

const getCornerRadius = (
  isMonday: boolean,
  isSunday: boolean,
  isFirstWeek: boolean,
  isLastWeek: boolean,
): string => {
  const leftRounded = `${BORDER_RADIUS} 0 0 ${BORDER_RADIUS}`;
  const rightRounded = `0 ${BORDER_RADIUS} ${BORDER_RADIUS} 0`;

  if (isFirstWeek && isLastWeek) {
    if (isMonday) return leftRounded;
    if (isSunday) return rightRounded;

    return "0";
  }

  if (isFirstWeek && isMonday) return leftRounded;
  if (isLastWeek && isSunday) return rightRounded;

  return "0";
};

const DAYS_IN_WEEK = 7;
const ISO_SUNDAY = 7;
const ISO_THURSDAY = 4;
const MS_PER_DAY = 86400000;
const ISO_WEEK_OFFSET = 10;

const getISOWeekNumberUTC = (date?: Date): number => {
  if (!date) return 0;

  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );

  const day = d.getUTCDay();
  const isoDay = day === 0 ? ISO_SUNDAY : day;

  d.setUTCDate(d.getUTCDate() + (ISO_THURSDAY - isoDay));

  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const diffDays = Math.floor((d.getTime() - yearStart.getTime()) / MS_PER_DAY);

  return Math.floor((diffDays + ISO_WEEK_OFFSET) / DAYS_IN_WEEK);
};

export const WeeksGrid: FC<WeeksGridProps> = ({
  selectedYear,
  selectedMonth,
  startDate,
  endDate,
  onDayClick,
  earliestSelectableDate,
}) => {
  const theme = useTheme();
  const [hoveredWeekStart, setHoveredWeekStart] = useState<Date | null>(null);

  const calendarDays = useMemo(
    () => generateCalendarDays(selectedYear, selectedMonth),
    [selectedYear, selectedMonth],
  );

  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }

    return result;
  }, [calendarDays]);

  const weekRange = useMemo(() => {
    if (!startDate) return null;

    const rangeStartWeek = getStartOfWeek(startDate);
    const rangeEndWeek = endDate ? getStartOfWeek(endDate) : null;

    return {
      start: rangeStartWeek.getTime(),
      end: rangeEndWeek?.getTime() ?? rangeStartWeek.getTime(),
    };
  }, [startDate, endDate]);

  const isWeekInRange = (date: Date): boolean => {
    if (!weekRange) return false;

    if (!endDate) {
      return isSameWeek(date, startDate!);
    }

    const weekStart = getStartOfWeek(date).getTime();

    return weekStart >= weekRange.start && weekStart <= weekRange.end;
  };

  const getBorderRadius = (
    date: Date,
    weekStart: number,
    isWeekHovered: boolean,
  ): string => {
    const isoDayOfWeek = toIsoDayOfWeek(getDay(date));
    const isMonday = isoDayOfWeek === 0;
    const isSunday = isoDayOfWeek === 6;

    if (!weekRange || !isWeekInRange(date)) {
      if (isWeekHovered) {
        return getCornerRadius(isMonday, isSunday, true, true);
      }

      return BORDER_RADIUS;
    }

    const isFirstWeek = weekStart === weekRange.start;
    const isLastWeek = weekStart === weekRange.end;

    return getCornerRadius(isMonday, isSunday, isFirstWeek, isLastWeek);
  };

  const isCurrentMonth = (date: Date): boolean =>
    getMonth(date) === selectedMonth && getYear(date) === selectedYear;

  const isHoveredWeek = (weekStart: Date): boolean =>
    hoveredWeekStart !== null && isSameWeek(hoveredWeekStart, weekStart);

  const getElementBackgroundColor = (
    inRange: boolean,
    isWeekHovered: boolean,
  ) => {
    if (inRange) {
      return isWeekHovered ? theme.colors.primary[6] : theme.colors.primary[5];
    }

    return isWeekHovered ? theme.colors.grey : "transparent";
  };

  const renderDayButton = (date: Date, isWeekHovered: boolean) => {
    const weekStart = getStartOfWeek(date).getTime();
    const inRange = isWeekInRange(date);
    const inCurrentMonth = isCurrentMonth(date);
    const dateKey = date.toISOString();
    const elementBackgroundColor = getElementBackgroundColor(
      inRange,
      isWeekHovered,
    );

    const isDateDisabled = (): boolean => {
      if (!earliestSelectableDate) return false;

      return getEndOfWeek(date) < earliestSelectableDate;
    };

    const getButtonColor = (isInRange: boolean, isInCurrentMonth: boolean) => {
      if (isInRange) return theme.colors.defaultBackground;
      if (isInCurrentMonth) return theme.colors.text[6];

      return theme.colors.text[3];
    };

    return (
      <Button
        disabled={isDateDisabled()}
        key={dateKey}
        onClick={() => onDayClick(date)}
        sx={{
          backgroundColor: elementBackgroundColor,
          color: getButtonColor(inRange, inCurrentMonth),
          "&:hover": {
            backgroundColor: elementBackgroundColor,
          },
          minWidth: 0,
          width: "100%",
          height: 32,
          borderRadius: getBorderRadius(date, weekStart, isWeekHovered),
          fontSize: 12,
          fontWeight: inCurrentMonth ? 500 : 400,
          position: "relative",
          p: 0,
          transition: "none",
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "inherit",
            fontWeight: "inherit",
          }}
        >
          {getDate(date)}
        </Typography>
      </Button>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "32px repeat(7, 1fr)",
          mb: 1,
        }}
      >
        <Box />
        {WEEKDAY_LABELS.map((label) => (
          <Box
            key={label}
            sx={{
              textAlign: "center",
              py: 1,
              fontSize: 10,
              fontWeight: 400,
              color: theme.colors.text[3],
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "grid", rowGap: 1 }}>
        {weeks.map((week) => {
          if (!week[0]) return null;

          const weekStart = getStartOfWeek(week[0]);
          const weekNumber = getISOWeekNumberUTC(weekStart);
          const isWeekHovered = isHoveredWeek(weekStart);

          return (
            <Box
              key={`${weekStart.toISOString()}`}
              onMouseEnter={() => setHoveredWeekStart(weekStart)}
              onMouseLeave={() => setHoveredWeekStart(null)}
              sx={{
                display: "grid",
                gridTemplateColumns: "32px repeat(7, 1fr)",
                rowGap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 400,
                  color: theme.colors.text[3],
                }}
              >
                {weekNumber}
              </Box>

              {week.map((day) => renderDayButton(day, isWeekHovered))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
